import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { instance } from '../api/instance';
import {
  DeliveryRateResponse,
  DeliveryRateType,
  DeliveryRateUnit,
} from '../types/delivery-rate.types';

interface DeliveryRateFormData {
  maxDistance: number;
  minDistance: number;
  minPrice: number;
  maxPrice: number;
  rate: number;
  isActive: boolean;
  unit: DeliveryRateUnit;
  rateType: DeliveryRateType;
  weatherMultiplier: number;
  isWeatherAffectingRate: boolean;
  surgeMultiplier: number;
  surgeThreshold: number;
  isSurgeAffectingRate: boolean;
  name: string;
}

export const useDeliveryRates = (
  onClose: () => void,
  deliveryRate?: DeliveryRateResponse | null
) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<DeliveryRateFormData>({
    maxDistance: 0,
    minDistance: 0,
    minPrice: 0,
    maxPrice: 0,
    rate: 100,
    isActive: true,
    unit: DeliveryRateUnit.METRE,
    rateType: DeliveryRateType.STANDARD,
    weatherMultiplier: 1.5,
    isWeatherAffectingRate: false,
    surgeMultiplier: 1.8,
    surgeThreshold: 1.5,
    isSurgeAffectingRate: false,
    name: '',
  });

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | { target: { name: string; value: any; type: string } }
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else if (type === 'number') {
      setFormData({
        ...formData,
        [name]: parseFloat(value),
      });
    } else if (type === 'select' && typeof value === 'boolean') {
      // Handle our custom select inputs for boolean values
      setFormData({
        ...formData,
        [name]: value,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      maxDistance: 0,
      minDistance: 0,
      minPrice: 0,
      maxPrice: 0,
      rate: 100,
      isActive: true,
      unit: DeliveryRateUnit.METRE,
      rateType: DeliveryRateType.STANDARD,
      weatherMultiplier: 1.5,
      isWeatherAffectingRate: true,
      surgeMultiplier: 1.8,
      surgeThreshold: 1.5,
      isSurgeAffectingRate: false,
      name: '',
    });
  };

  // Mutation for creating a new delivery rate
  const createRateMutation = useMutation<
    DeliveryRateResponse,
    Error,
    DeliveryRateFormData
  >(
    (data) =>
      instance
        .post('/admin/delivery-rate/create/', data)
        .then((res) => res.data.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['delivery-rates']);
        onClose();
        resetForm();
      },
    }
  );

  const updateRateMutation = useMutation<
    DeliveryRateResponse,
    Error,
    { id: string | number; data: DeliveryRateFormData }
  >(
    ({ id, data }) =>
      instance
        .put(`/admin/delivery-rate/${id}`, data)
        .then((res) => res.data.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['delivery-rates']);
        onClose();
        resetForm();
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (deliveryRate) {
      updateRateMutation.mutate({
        id: deliveryRate.id,
        data: formData,
      });
    } else {
      createRateMutation.mutate(formData);
    }
  };

  React.useEffect(() => {
    if (deliveryRate) {
      setFormData(deliveryRate);
    }
  }, [deliveryRate]);

  return {
    formData,
    setFormData,
    isSubmitting: createRateMutation.isLoading || updateRateMutation.isLoading,
    isSuccess: createRateMutation.isSuccess || updateRateMutation.isSuccess,
    error: createRateMutation.error || updateRateMutation.error,
    handleInputChange,
    handleSubmit,
    resetForm,
  };
};
